import * as React from 'react';
import { mergeStyles } from '@cian/utils';

import { DOM_RF_TITLE } from './links';

export interface IDropdownMenuLink {
  url: string;
  text: JSX.Element | string;
  target?: string;
  onClick?: () => void;
};

export interface IDropdownMenuProps {
  parentElement: Element;
  links: IDropdownMenuLink[];
  alwaysOpened?: boolean;
  hideShadow?: boolean;
};

export interface IDropdownMenuState {
  parentOver?: boolean;
  mouseInTriangle?: boolean;
  dropdownOver?: boolean;
};

interface ICoords {
  x: number;
  y: number;
}

interface ITriangle {
  top: ICoords;
  left: ICoords;
  right: ICoords;
};

const styles = require('./dropdown_menu.css');

export default class DropdownMenu extends React.Component<IDropdownMenuProps, IDropdownMenuState> {
  private parentElementRect: ClientRect;
  private DELAY = 300;
  private containerElement: HTMLDivElement;
  private triangle: ITriangle = {} as ITriangle;

  public constructor(props: IDropdownMenuProps) {
    super(props);

    this.state = {
      parentOver: false,
    };

    this.mouseMoveDocument = this.mouseMoveDocument.bind(this);
  };

  public componentWillMount() {
    if (this.props.alwaysOpened) {
      return;
    }
    this.parentElementRect = this.props.parentElement.getBoundingClientRect();
    this.props.parentElement.addEventListener('mouseenter', (event: MouseEvent) => {
      this.setState({
        parentOver: true,
      }, () => {
        this.createTriangle();
        document.addEventListener('mousemove', this.mouseMoveDocument);
      });
    });

    this.props.parentElement.addEventListener('mouseout', (event: MouseEvent) => {
      this.setState({
        parentOver: false,
      }, () => {
        document.removeEventListener('mousemove', this.mouseMoveDocument);
        setTimeout(() => !this.state.parentOver && this.setState({ mouseInTriangle: false }), this.DELAY)
      });
    });
  }

  public render() {
    if (this.props.alwaysOpened) {
      return this.renderAlwaysOpened();
    }
    return (
      <div className={`${styles['container']} ${styles['container--scale']} ${
        (this.state.parentOver || this.state.mouseInTriangle || this.state.dropdownOver)
          && styles['container--scale_over']
        } cf_header-bottom_menu_item-dropdown`}
        ref={(el) => this.containerElement = el}
        onMouseEnter={(e) => this.dropdownMouseEnter(e)}
        onMouseLeave={(e) => this.dropdownMouseOut(e)}>
        <div className="cg-container-fluid-xs">
          <div className="cg-row">
            <div className="cg-skip-3 cg-col-21">
              <div className={styles['list']}>
                {this.renderLinks()}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  public renderAlwaysOpened() {
    return (
      <div className={`${styles['container']} ${styles['container--scale']} ${styles['container--scale_over']}
        ${styles['container--always_opened']} ${(this.props.hideShadow && styles['container--no_shadow'])}`}
        ref={(el) => this.containerElement = el}>
        <div className="cg-container-fluid-xs">
          <div className="cg-row">
            <div className="cg-skip-3 cg-col-21">
              <div className={styles['list']}>
                {this.renderLinks()}
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  private renderLinks() {
    return this.props.links.map(({
      onClick,
      target,
      text,
      url,
    }, index) => (
      <div key={`link-${index}`} className={styles['item']}>
        <a
          href={url}
          {...mergeStyles(text === DOM_RF_TITLE && styles['domrf-promo'], styles['link'])}
          onClick={(event) => this.onLinkClick({
            event,
            onClick,
            target,
            url,
          })}
          target={target || '_self'}>{text}</a>
      </div>
    ));
  }

  private dropdownMouseEnter(e: React.MouseEvent<HTMLDivElement>) {
    this.setState({
      dropdownOver: true,
    });
  }

  private dropdownMouseOut(e: React.MouseEvent<HTMLDivElement>) {
    this.setState({
      dropdownOver: false,
    });
  }

  private mouseMoveDocument(e: MouseEvent) {
    if (this.checkMouseInTriangle({ x: e.pageX, y: e.pageY})) {
      this.setState({
        mouseInTriangle: true,
      });
    } else {
      this.setState({
        mouseInTriangle: false,
      });
    }
  }

  private createTriangle() {
    const containerRect = this.containerElement.getBoundingClientRect();
    const parentRect = this.props.parentElement.getBoundingClientRect();

    this.triangle.top = {
      x: parentRect.left + document.body.scrollLeft + (parentRect.width / 2),
      y: parentRect.top + document.body.scrollTop + (parentRect.height / 2),
    };

    this.triangle.left = {
      x: containerRect.left + document.body.scrollLeft,
      y: containerRect.top + document.body.scrollTop,
    };

    this.triangle.right = {
      x: this.triangle.top.x + containerRect.width,
      y: this.triangle.top.y,
    };
  }

  private checkMouseInTriangle(coords: ICoords) {
    const { left, top, right } = this.triangle;

    const a = (left.x - coords.x) * (top.y - left.y) - (top.x - left.x) * (left.y - coords.y);
    const b = (top.x - coords.x) * (right.y - top.y) - (right.x - top.x) * (top.y - coords.y);
    const c = (right.x - coords.x) * (left.y - right.y) - (left.x - right.x) * (right.y - coords.y);

    return (a >= 0 && b >= 0 && c >= 0) || (a <= 0 && b <= 0 && c <= 0);
  }

  private onLinkClick = ({
    event,
    onClick,
    url,
    target
  } : {
    event: React.MouseEvent<HTMLAnchorElement>;
    url: string;
    target?: string;
    onClick?: () => void;
  }) => {
    event.preventDefault();
    if (onClick) {
      onClick();
    }
    target === '_blank'
      ? window.open(url)
      : location.replace(url);
  }
}



// WEBPACK FOOTER //
// ./src/bundles/header/components/dropdown_menu/dropdown_menu.tsx